import { Head, Link, router } from '@inertiajs/react'
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { ArrowLeft, Users, FileText, Edit, X } from "lucide-react"
import { DealsTable } from "@/components/deals-table"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { FlashMessage } from '@/components/flash-message'

interface User {
  id: number
  name: string
  email: string
  role: string
}

interface Team {
  id: number
  name: string
  salesforce_id: string
}

interface Props {
  team: Team
  team_members: User[]
  available_users: User[]
  deals: any[]
}

export default function Show({ team, team_members, available_users, deals }: Props) {
  const handleAddMember = (userId: string) => {
    router.post(`/teams/${team.id}/add_member`, {
      user_id: userId
    }, {
      only: ['team_members', 'available_users', 'flash'],
      preserveScroll: true,
      onSuccess: () => {
        // Reset the select
        const select = document.querySelector('select') as HTMLSelectElement
        if (select) select.value = ''
      }
    })
  }

  const handleRemoveMember = (userId: number) => {
    router.delete(`/teams/${team.id}/remove_member`, {
      data: { user_id: userId },
      only: ['team_members', 'available_users', 'flash'],
      preserveScroll: true
    })
  }

  return (
    <>
      <Head title={`Team: ${team.name}`} />
      <div className="">
        {/* Header with navigation and actions */}
        <div className="flex items-center justify-between border-b border-b-gray-100 p-4 mb-0">
          <div className="flex items-center space-x-4">
            <Link href="/teams">
              <Button variant="outline" size="icon">
                <ArrowLeft className="h-4 w-4" />
              </Button>
            </Link>
            <div>
              <h1 className="text-2xl font-bold tracking-tight">
                {team.name}
              </h1>
              <p className="text-sm text-muted-foreground">
                Salesforce ID: {team.salesforce_id}
              </p>
            </div>
          </div>
        </div>

        {/* Main content */}
        <div className="">
          <div className="grid grid-cols-1 md:grid-cols-4 min-h-screen">
            {/* Main content area - with deals table */}
            <div className="md:col-span-3">
              <DealsTable
                title='Deals'
                hiddenColumns={["team.name"]}
              />
            </div>

            {/* Right sidebar with team information */}
            <div className="space-y-6 p-6 bg-gray-50/50 border-l border-l-gray-100 p-4 md:col-span-1 min-h-full">
              {/* Basic Information Card */}
              <div>
                <h2 className="text-lg font-semibold mb-4 flex items-center">
                  Team Information
                </h2>
                <div className="space-y-4">
                  <dl className="grid grid-cols-2 gap-2 text-sm">
                    <dt className="text-muted-foreground">Name</dt>
                    <dd className="font-medium">{team.name || '-'}</dd>

                    <dt className="text-muted-foreground">Salesforce ID</dt>
                    <dd className="font-medium">{team.salesforce_id || '-'}</dd>
                  </dl>
                </div>
              </div>

              {/* Team Members Card */}
              <div>
                <h2 className="text-lg font-semibold mb-4 flex items-center">
                  Portfolio Members
                </h2>
                <div className="space-y-4">
                  {/* Current Members */}
                  <div className="space-y-2">
                    <div className="space-y-2">
                      {team_members.map((member) => (
                        <div key={member.id} className="flex items-center justify-between p-2 border rounded-lg">
                          <div>
                            <Link href={`/users/${member.id}`} className="font-medium hover:underline">{member.name}</Link>
                            <p className="text-sm text-gray-500">{member.email}</p>
                          </div>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleRemoveMember(member.id)}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Add Member */}
                  { available_users.length > 0 && (
                  <div className="space-y-2">
                    <Select onValueChange={handleAddMember}>
                      <SelectTrigger>
                        <SelectValue placeholder="Add user" />
                      </SelectTrigger>
                      <SelectContent>
                        {available_users.map((user) => (
                          <SelectItem key={user.id} value={user.id.toString()}>
                            {user.name} ({user.email})
                          </SelectItem>
                        ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
